import type { AxiosRequestConfig } from 'axios';
import { branchScope, get, postAllPages, setBranchResolver } from '@/lib/apiClient';
import { endpoints } from './endpoints';

// Each branch has one school. The API's branch-scoped routes take the branch in
// X-Branch-Id instead of a schoolId in the path, so school-scoped calls map the
// school the UI works in to its branch here. Schools read through api/school.ts
// are remembered; an unknown school is read once.

const branchBySchool = new Map<number, number>();
/** The organization's schools in list order, as last read (the header's default school is the first). */
let schoolList: { schoolId: number; branchId: number }[] | null = null;

/** The school list read in flight, shared by every request that starts before it answers. */
let schoolListRead: Promise<{ schoolId: number; branchId: number }[]> | null = null;
/** Bumped by every reset, so a read that started before it is not remembered. */
let scopeGeneration = 0;

/** Discard mappings whenever the authenticated identity or organization/branch changes. */
export function resetSchoolScope(): void {
  scopeGeneration++;
  branchBySchool.clear();
  schoolList = null;
  schoolListRead = null;
  chosenSchoolId = null;
}

/** Remembers the organization's school list, in order, and the branch of each school. */
export function rememberSchoolList(
  schools: readonly { schoolId: number; branchId: number }[],
): void {
  schoolList = schools.map(({ schoolId, branchId }) => ({ schoolId, branchId }));
  rememberSchoolBranches(schools);
}

/** Remembers the branch of every school the UI has read. */
export function rememberSchoolBranches(
  schools: readonly { schoolId: number; branchId: number }[],
): void {
  for (const school of schools) branchBySchool.set(school.schoolId, school.branchId);
}

/** The branch of a school: from the schools already read, else read from the API. */
export async function branchOfSchool(schoolId: number): Promise<number> {
  const known = branchBySchool.get(schoolId);
  if (known != null) return known;
  const school = await get<{ schoolId: number; branchId: number }>(
    endpoints.foundation.school(schoolId),
  );
  rememberSchoolBranches([school]);
  return school.branchId;
}

/**
 * The branch of the school the header works in: the chosen school when it still
 * exists, else the organization's first school (as useCurrentSchool picks it).
 * Reads the school list when it has not been read yet.
 */
export async function currentSchoolBranch(): Promise<number | null> {
  let list = schoolList;
  if (!list) {
    const generation = scopeGeneration;
    // Requests that start together share one read instead of each reading the list.
    schoolListRead ??= postAllPages<{ schoolId: number; branchId: number }>(endpoints.foundation.schoolSearch).then(
      (rows) => rows.map(({ schoolId, branchId }) => ({ schoolId, branchId })),
    );
    const read = schoolListRead;
    try {
      list = await read;
    } finally {
      if (schoolListRead === read) schoolListRead = null;
    }
    // A reset (sign-out, organization switch) while the read was out keeps the list unset.
    if (generation === scopeGeneration) schoolList ??= list;
  }
  const school = list.find((s) => s.schoolId === chosenSchoolId) ?? list[0];
  return school?.branchId ?? null;
}

let chosenSchoolId: number | null = null;

/** The school chosen in the header (GlobalStateProvider keeps it current); null follows the default. */
export function setChosenSchool(schoolId: number | null): void {
  chosenSchoolId = schoolId;
}

/**
 * Organization-wide routes: they must not name a branch. The school list (the
 * header picks from it), the organization's global settings and key formats
 * and letterheads (a branch user may not change them), the audit log and the
 * central documents.
 */
export const ORGANIZATION_ROUTES =
  /^\/api\/(schools(\/|\?|$)|global-settings|key-formats|letterheads|audit-logs|documents|uploads)/;

/**
 * Every other API call acts for a branch: when it names none itself (`schoolScope`,
 * `branchScope`, the request context), it gets the branch of the school chosen in
 * the header, else of the organization's first school.
 */
export const actsForCurrentSchool = (url: string) =>
  url.startsWith('/api/') && !ORGANIZATION_ROUTES.test(url);

setBranchResolver((url) => (actsForCurrentSchool(url) ? currentSchoolBranch() : null));

/** Request config that sends the school's branch in X-Branch-Id. */
export const schoolScope = async (schoolId: number): Promise<AxiosRequestConfig> =>
  branchScope(await branchOfSchool(schoolId)) ?? {};
